/**
 * Presença unificada — vendedor e fornecedor.
 *
 * storeOnline = intenção do vendedor (toggle).
 * isStoreEffectivelyOnline = realidade (intenção + TTL quando flag ligada).
 *
 * Flag PRESENCE_TTL_ENFORCED / VITE_PRESENCE_TTL_ENFORCED (default false):
 * comportamento idêntico ao histórico até ligar a flag.
 */
import { readPresenceTtlEnforced } from './presenceFlags';
import { PRESENCE_TTL_MS } from '../shared/presenceConstants';

export { PRESENCE_TTL_MS };

/** Legacy only — docs sem flag online. */
export const STORE_PRESENCE_TTL_MS = 10 * 60 * 1000;

/** Alias para compatibilidade com código do vendedor. */
export const VENDOR_PRESENCE_TTL_MS = STORE_PRESENCE_TTL_MS;

/** Intervalo do heartbeat com dashboard aberto (2 min). */
export const VENDOR_PRESENCE_HEARTBEAT_MS = 120 * 1000;

/** Servidor pode ignorar touch se lastSeen mais recente que isto. */
export const VENDOR_PRESENCE_TOUCH_MIN_MS = 90 * 1000;

export function parseLastSeenMs(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof value === 'number') {
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === 'object') {
    const v = value as { toDate?: () => Date; _seconds?: number };
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (typeof v._seconds === 'number') return v._seconds * 1000;
  }
  return 0;
}

export type StorePresenceEntity = {
  id?: string;
  storeOnline?: boolean;
  isOpen?: boolean;
  isOnline?: boolean;
  lastSeenAt?: unknown;
  lastSeen?: unknown;
  /** Marcado pelo cron quando lastSeen expirou (não sobrescreve storeOnline). */
  storeEffectiveOffline?: boolean;
  /** Vendas pausadas pelo OWNER (acerto em aberto). Só o servidor grava. */
  salesPaused?: boolean;
} | null | undefined;

function resolveOnlineFlag(entity: StorePresenceEntity): boolean | undefined {
  if (!entity) return undefined;
  if (entity.storeOnline === true) return true;
  if (entity.storeOnline === false) return false;
  if (entity.isOpen === true) return true;
  if (entity.isOpen === false) return false;
  if (entity.isOnline === true) return true;
  if (entity.isOnline === false) return false;
  return undefined;
}

function resolveLastSeenMs(entity: StorePresenceEntity): number {
  if (!entity) return 0;
  return parseLastSeenMs(entity.lastSeenAt) || parseLastSeenMs(entity.lastSeen);
}

/** Comportamento histórico (flag off). */
export function isStoreOpenLegacy(entity: StorePresenceEntity, debug = false): boolean {
  const tag = '[store-presence]';
  const id = entity?.id ?? '?';
  const online = resolveOnlineFlag(entity);
  const lastSeenMs = resolveLastSeenMs(entity);

  if (debug) {
    console.debug(tag, 'legacy check', {
      id,
      storeOnline: entity?.storeOnline,
      lastSeenMs,
      online,
    });
  }

  if (online === true) {
    if (debug) console.debug(tag, 'PASS: flag online', { id });
    return true;
  }
  if (online === false) {
    if (debug) console.debug(tag, 'FAIL: flag offline', { id });
    return false;
  }

  // Loja que nunca registrou presença conta como FECHADA (abre pelo app do vendedor).
  if (lastSeenMs === 0) {
    if (debug) console.debug(tag, 'FAIL: nunca registrou presença', { id });
    return false;
  }

  const ageMs = Date.now() - lastSeenMs;
  const open = ageMs <= STORE_PRESENCE_TTL_MS;
  if (debug) {
    console.debug(tag, open ? 'PASS legacy TTL' : 'FAIL legacy TTL', { id, ageMs });
  }
  return open;
}

/**
 * Realidade operacional da loja.
 * Flag off → idêntico a isStoreOpenLegacy.
 * Flag on → storeOnline===true E lastSeen≤PRESENCE_TTL_MS.
 * storeEffectiveOffline (sweep/beacon) só reforça fechamento se lastSeen também expirou.
 * Nunca sobrescreve o campo storeOnline do vendedor.
 */
export function isStoreEffectivelyOnline(
  entity: StorePresenceEntity,
  nowMs: number = Date.now(),
  debug = false
): boolean {
  if (entity?.salesPaused === true) return false;
  if (!readPresenceTtlEnforced()) {
    return isStoreOpenLegacy(entity, debug);
  }

  const tag = '[store-presence-ttl]';
  const id = entity?.id ?? '?';
  const online = resolveOnlineFlag(entity);

  if (online === false) {
    if (debug) console.debug(tag, 'FAIL: intenção offline', { id });
    return false;
  }

  if (online !== true) {
    // Sem intenção explícita: legado
    return isStoreOpenLegacy(entity, debug);
  }

  const lastSeenMs = resolveLastSeenMs(entity);
  if (lastSeenMs === 0) {
    if (debug) console.debug(tag, 'FAIL: online sem lastSeen', { id });
    return false;
  }

  const ageMs = nowMs - lastSeenMs;
  const withinTtl = ageMs <= PRESENCE_TTL_MS;
  // Flag sticky do sweep/beacon só vale se lastSeen também expirou.
  // Heartbeat fresco anula storeEffectiveOffline (evita loja "fechada" online).
  if (entity?.storeEffectiveOffline === true && !withinTtl) {
    if (debug) console.debug(tag, 'FAIL: storeEffectiveOffline + lastSeen expirado', { id, ageMs });
    return false;
  }
  if (debug) {
    console.debug(tag, withinTtl ? 'PASS TTL' : 'FAIL TTL', {
      id,
      ageMs,
      PRESENCE_TTL_MS,
      storeEffectiveOffline: entity?.storeEffectiveOffline,
    });
  }
  return withinTtl;
}

/** Loja aceita pedidos agora. NÃO usar para filtrar vitrine sem passar por isto. */
export function isStoreOpen(entity: StorePresenceEntity, debug = false): boolean {
  return isStoreEffectivelyOnline(entity, Date.now(), debug);
}

export function logStorePresenceCheck(
  label: string,
  entity: StorePresenceEntity
): boolean {
  const open = isStoreOpen(entity, true);
  console.debug(`[store-presence] ${label}`, {
    supplierId: entity?.id,
    storeOnline: entity?.storeOnline,
    isOpen: entity?.isOpen,
    isOnline: entity?.isOnline,
    lastSeenAt: entity?.lastSeenAt,
    lastSeen: entity?.lastSeen,
    storeEffectiveOffline: entity?.storeEffectiveOffline,
    isStoreOpen: open,
  });
  return open;
}
